import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getKanbanRegua } from '../../api/carsApi';
import { useHomeCarsQuery } from '../../infrastructure/react-query/hooks/useHomeQueries';
import { queryKeys } from '../../infrastructure/react-query/queryKeys';
import { useEsteiras } from '../../hooks/useEsteiras';
import { notify } from '../../shared/feedback/notify';
import { useTemaResolvido } from '../../shared/tema/tema';
import { CLASSES_DE_VEICULO, corDaClasse, nomeDaClasse } from '../../shared/ui/cor-da-classe';
import LogoSoac from '../../components/logo-soac/LogoSoac';
import { dataSegura, estaParado, horasSemMovimentacao } from '../home/home.util';
import { derivarReguaDoVeiculo, montarBaseDaRegua, EstadoDoChip } from '../home/regua.util';
import { agruparEmFases } from './fases';
import { siglaDaEsteira } from './siglas';
import ColunaDaFase from './ColunaDaFase';
import Relogio, { useRelogio } from './Relogio';
import type { EstadoNaTrilha, PontoDaTrilha, VeiculoDoTelao } from './PlacaVeiculo';

import '../../shared/feedback/erro-de-tela.css';
import './TelaoOficina.css';

/**
 * O Telão da oficina (at-130, 23/10/2026): as esteiras agrupadas em 5 fases, os veículos como
 * placas, e as colunas trocando de página sozinhas — feito para ficar aberto na TV.
 *
 * Mesmo componente nos dois lugares: a aba "Telão" da Home (`modo="aba"`) e a rota `/telao` em
 * tela cheia (`modo="telaCheia"`, com relógio e atualização a cada 60 s). O tema é o do usuário.
 *
 * Reusa as queries da Home: herda a atualização por socket (`useCarStatusSocket` invalida
 * `cars`) e não cria rota nenhuma no backend.
 */

interface TelaoOficinaProps {
  modo: 'aba' | 'telaCheia';
  onAbrirVeiculo: (carId: string) => void;
  /** Com o modal aberto a troca de página para — senão o quadro muda por trás de quem está lendo. */
  modalAberto: boolean;
  /** Só na aba: leva à rota em tela cheia. */
  onAbrirTelaCheia?: () => void;
  /** Só na tela cheia: volta à Home. */
  onSair?: () => void;
  onAjuda?: () => void;
  /** Na aba da Home: as abas "Quadro de esteiras / Telão", desenhadas no cabeçalho, entre o logo e os indicadores. */
  abas?: React.ReactNode;
}

const INTERVALOS = [6, 10, 12, 20, 30, 60];
const INTERVALO_PADRAO = 12;
const ATUALIZACAO_TELA_CHEIA_MS = 60_000;
const CHAVE_SEGUNDOS = 'soac-telao-segundos';

const ESTADO_NA_TRILHA: Partial<Record<EstadoDoChip, EstadoNaTrilha>> = {
  atual: 'atual',
  realizada: 'feita',
  inacabada: 'inacabada',
  paralela: 'paralela',
  prevista: 'prevista',
};

function lerPreferencia(chave: string): string | null {
  try {
    return window.localStorage.getItem(chave);
  } catch {
    // Sem armazenamento (janela privada, bloqueio): o telão funciona com o padrão.
    return null;
  }
}

function gravarPreferencia(chave: string, valor: string) {
  try {
    window.localStorage.setItem(chave, valor);
  } catch {
    // Preferência perdida ao recarregar; nada mais quebra.
  }
}

function diasNaOficina(car: any, agora: Date): number | null {
  if (typeof car.daysInShop === 'number') return car.daysInShop;
  const entrada = dataSegura(car.entryDate);
  if (!entrada) return null;
  return Math.max(0, Math.floor((agora.getTime() - entrada.getTime()) / 86_400_000));
}


const TelaoOficina: React.FC<TelaoOficinaProps> = ({ modo, onAbrirVeiculo, modalAberto, onAbrirTelaCheia, onSair, onAjuda, abas }) => {
  const telaCheia = modo === 'telaCheia';
  const refetchInterval = telaCheia ? ATUALIZACAO_TELA_CHEIA_MS : false;

  const carrosQuery = useHomeCarsQuery({ refetchInterval });
  const { esteirasAtivas, isLoading: carregandoEsteiras, isError: erroEsteiras } = useEsteiras();

  const carros = useMemo(() => (carrosQuery.data || []) as any[], [carrosQuery.data]);
  const idsVisiveis = useMemo(() => {
    const ativas = new Set(esteirasAtivas.filter((e) => e.chave !== 'ENTREGA').map((e) => e.id));
    return carros.filter((c) => ativas.has(c.esteiraId)).map((c) => String(c.id)).sort();
  }, [carros, esteirasAtivas]);

  const reguaQuery = useQuery({
    queryKey: queryKeys.cars.kanbanRegua(idsVisiveis),
    queryFn: () => getKanbanRegua(idsVisiveis),
    enabled: idsVisiveis.length > 0,
    staleTime: 60_000,
    refetchInterval,
  });

  // Falha de carga não pode virar quadro vazio: "Nenhum veículo" em todas as fases é a leitura
  // errada mais cara desta tela (at-60).
  useEffect(() => {
    if (carrosQuery.isError) {
      notify.errorFrom(
        carrosQuery.error,
        'Não foi possível carregar os veículos do telão. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'telao-carros' },
      );
    }
  }, [carrosQuery.isError, carrosQuery.error]);

  useEffect(() => {
    if (reguaQuery.isError) {
      notify.warning(
        'Não foi possível carregar as etapas dos veículos. As placas aparecem sem a trilha até a próxima atualização.',
        { toastId: 'telao-regua' },
      );
    }
  }, [reguaQuery.isError]);

  const agora = useMemo(() => new Date(), [carrosQuery.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const veiculos: VeiculoDoTelao[] = useMemo(() => {
    const porId = new Map((reguaQuery.data ?? []).map((item) => [item.carId, item]));
    const esteiraPorId = new Map(esteirasAtivas.map((e) => [e.id, e]));
    const semEntrega = esteirasAtivas.filter((e) => e.chave !== 'ENTREGA');

    return carros.map((car) => {
      const item = porId.get(car.id);
      let trilha: PontoDaTrilha[] | null = null;
      if (item && !reguaQuery.isError) {
        trilha = derivarReguaDoVeiculo(car, montarBaseDaRegua(semEntrega, item), item.planejadas ?? [])
          .filter((chip) => {
            const esteira = esteiraPorId.get(chip.id);
            return esteira && !esteira.sistema && !chip.oculto && !chip.foraDoPlanejamento;
          })
          .flatMap((chip) => {
            const estado = ESTADO_NA_TRILHA[chip.estado];
            const esteira = esteiraPorId.get(chip.id)!;
            return estado ? [{ esteiraId: chip.id, nome: chip.nome, sigla: siglaDaEsteira(esteira), estado }] : [];
          });
      }
      const liberacao =
        car.releaseStatus === 'TOTAL' || (!car.releaseStatus && car.releasedAt)
          ? 'TOTAL'
          : car.releaseStatus === 'PARCIAL'
            ? 'PARCIAL'
            : null;
      return {
        id: String(car.id),
        esteiraId: car.esteiraId ?? null,
        placa: car.plate || '-',
        modelo: car.model || '',
        cor: car.color || '',
        classe: nomeDaClasse(car.carClass),
        corDaClasse: corDaClasse(car.carClass),
        dias: diasNaOficina(car, agora),
        horasParado: horasSemMovimentacao(car, agora),
        liberacao,
        trilha,
      } as VeiculoDoTelao;
    });
  }, [carros, esteirasAtivas, reguaQuery.data, reguaQuery.isError, agora]);

  const fases = useMemo(() => agruparEmFases(esteirasAtivas, veiculos), [esteirasAtivas, veiculos]);

  const kpis = useMemo(() => {
    const naOficina = fases.flatMap((f) => f.esteiras.flatMap((e) => e.carros));
    const ids = new Set(naOficina.map((v) => v.id));
    const brutos = carros.filter((c) => ids.has(String(c.id)));
    const comDias = naOficina.filter((v) => v.dias !== null);
    return {
      total: naOficina.length,
      parados: brutos.filter((c) => estaParado(c, agora)).length,
      liberados: naOficina.filter((v) => v.liberacao === 'TOTAL').length,
      media: comDias.length ? Math.round(comDias.reduce((s, v) => s + (v.dias ?? 0), 0) / comDias.length) : null,
      aguardandoEntrega: fases
        .flatMap((f) => f.esteiras)
        .filter((e) => e.esteira.chave === 'AGUARDANDO_ENTREGA')
        .reduce((n, e) => n + e.carros.length, 0),
    };
  }, [fases, carros, agora]);

  // ---------- Troca de página ----------
  const [segundos, setSegundos] = useState<number>(() => {
    const salvo = Number(lerPreferencia(CHAVE_SEGUNDOS));
    return INTERVALOS.includes(salvo) ? salvo : INTERVALO_PADRAO;
  });
  const [pausado, setPausado] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [decorrido, setDecorrido] = useState(0);
  const [paginasPorFase, setPaginasPorFase] = useState<Record<string, number>>({});

  const registrarPaginas = useCallback((faseId: string, quantidade: number) => {
    setPaginasPorFase((atual) => (atual[faseId] === quantidade ? atual : { ...atual, [faseId]: quantidade }));
  }, []);

  const totalPaginas = Math.max(1, ...fases.map((f) => paginasPorFase[f.fase.id] ?? 1));
  const paginaAtual = pagina % totalPaginas;
  const parado = pausado || modalAberto || totalPaginas < 2;

  useEffect(() => {
    if (parado) return undefined;
    const t = setInterval(() => {
      setDecorrido((d) => {
        if (d + 1 >= segundos) {
          setPagina((p) => p + 1);
          return 0;
        }
        return d + 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [parado, segundos]);

  // Clicar num segmento leva àquela página e reinicia a contagem da troca.
  const irParaPagina = (i: number) => {
    setPagina(i);
    setDecorrido(0);
  };

  const trocarIntervalo = (valor: number) => {
    setSegundos(valor);
    setDecorrido(0);
    gravarPreferencia(CHAVE_SEGUNDOS, String(valor));
  };

  // ---------- Relógio e tema ----------
  const relogio = useRelogio();

  // O telão segue o tema do usuário (escolhido no menu lateral) — não tem botão próprio.
  const tema = useTemaResolvido();

  const minutosDesdeAtualizacao = carrosQuery.dataUpdatedAt
    ? Math.max(0, Math.floor((relogio.getTime() - carrosQuery.dataUpdatedAt) / 60_000))
    : null;

  const carregando = carrosQuery.isLoading || carregandoEsteiras;
  const falhou = carrosQuery.isError || erroEsteiras;

  return (
    <div className={`telao telao--${modo}`} data-tema={tema}>
      <header className="telao-topo">
        <div className="telao-marca">
          <LogoSoac tamanho={44} corDoFundo="var(--telao-fundo)" />
          {onAjuda && (
            <button type="button" className="help-circle-btn" title="Como Funciona" onClick={onAjuda}>
              ?
            </button>
          )}
        </div>
        <div className="telao-kpis" aria-live="polite">
          <div className="telao-kpi">
            <b>{carregando || falhou ? '–' : kpis.total}</b>
            <span>veículos na oficina</span>
          </div>
          <div className="telao-kpi telao-kpi--critico">
            <b>{carregando || falhou ? '–' : kpis.parados}</b>
            <span>parados há mais de 48 h</span>
          </div>
          <div className="telao-kpi telao-kpi--ok">
            <b>{carregando || falhou ? '–' : kpis.liberados}</b>
            <span>liberados pela administração</span>
          </div>
          <div className="telao-kpi">
            <b>{carregando || falhou || kpis.media === null ? '–' : kpis.media}</b>
            <span>dias em média na oficina</span>
          </div>
          <div className="telao-kpi telao-kpi--destaque">
            <b>{carregando || falhou ? '–' : kpis.aguardandoEntrega}</b>
            <span>aguardando entrega</span>
          </div>
        </div>
        <Relogio className="telao-relogio" agora={relogio} />
        {/* Barra de controle: as abas da Home (na aba), o andamento das páginas e os botões. */}
        <div className="telao-barra">
          {abas && <div className="telao-abas">{abas}</div>}
          <div className="telao-paginador" aria-live="off">
            {totalPaginas > 1 ? (
              <>
                <span className="telao-paginador-pontos">
                  {Array.from({ length: totalPaginas }, (_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={i === paginaAtual ? 'telao-ponto telao-ponto--ativo' : 'telao-ponto'}
                      onClick={() => irParaPagina(i)}
                      aria-label={`Ir para a página ${i + 1}`}
                      aria-current={i === paginaAtual ? 'page' : undefined}
                      title={`Página ${i + 1}`}
                    />
                  ))}
                </span>
                <span>
                  página <strong>{paginaAtual + 1}</strong> de <strong>{totalPaginas}</strong> ·{' '}
                  {pausado ? (
                    <strong>pausado</strong>
                  ) : modalAberto ? (
                    <strong>em espera</strong>
                  ) : (
                    <>
                      troca em <strong className="telao-contagem">{segundos - decorrido} s</strong>
                    </>
                  )}
                </span>
              </>
            ) : (
              <span>tudo cabe numa página só</span>
            )}
          </div>
        </div>
      </header>

      {falhou ? (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar os veículos do telão. O quadro não está sendo exibido para não parecer vazio.
          Recarregue a página; se persistir, acione a TI.
        </div>
      ) : carregando ? (
        <p className="telao-carregando">Carregando o quadro da oficina…</p>
      ) : (
        <main className="telao-quadro" style={{ gridTemplateColumns: `repeat(${fases.length}, minmax(0, 1fr))` }}>
          {fases.map((f) => (
            <ColunaDaFase
              key={f.fase.id}
              fase={f}
              paginaGlobal={paginaAtual}
              onPaginas={registrarPaginas}
              onAbrirVeiculo={onAbrirVeiculo}
            />
          ))}
        </main>
      )}

      <footer className="telao-rodape">
        <div className="telao-legenda">
          {CLASSES_DE_VEICULO.map((c) => (
            <span key={c.chave} className="telao-legenda-item">
              <i className="telao-legenda-cor" style={{ background: corDaClasse(c.chave).fundo }} />
              {c.nome}
            </span>
          ))}
          <i className="telao-legenda-divisor" />
          <span className="telao-legenda-item"><i className="telao-trilha-marca telao-legenda-ponto--feita" />Etapa feita</span>
          <span className="telao-legenda-item"><i className="telao-trilha-marca telao-legenda-ponto--inacabada" />Inacabada</span>
          <span className="telao-legenda-item"><i className="telao-trilha-marca telao-legenda-ponto--atual" />Etapa atual</span>
          <span className="telao-legenda-item"><i className="telao-trilha-marca telao-legenda-ponto--paralela" />Em paralelo</span>
          <span className="telao-legenda-item"><i className="telao-trilha-marca telao-legenda-ponto--prevista" />Prevista</span>
          <i className="telao-legenda-quebra" aria-hidden="true" />
          <span className="telao-legenda-item"><i className="telao-selo telao-selo--atencao">3 d parado</i>mais de 48 h sem mudar de esteira</span>
          <span className="telao-legenda-item"><i className="telao-selo telao-selo--critico">6 d parado</i>mais de 5 dias</span>
          <span className="telao-legenda-item"><i className="telao-selo telao-selo--liberado">Liberado</i>liberação administrativa</span>
        </div>
        {telaCheia && minutosDesdeAtualizacao !== null && (
          <span className="telao-atualizado">
            Atualizado {minutosDesdeAtualizacao === 0 ? 'agora' : `há ${minutosDesdeAtualizacao} min`}
          </span>
        )}
        <div className="telao-controles">
          <label className="telao-controle-rotulo" htmlFor={`telao-segundos-${modo}`}>Troca a cada</label>
          <select
            id={`telao-segundos-${modo}`}
            className="telao-botao"
            value={segundos}
            onChange={(e) => trocarIntervalo(Number(e.target.value))}
          >
            {INTERVALOS.map((s) => (
              <option key={s} value={s}>{s} s</option>
            ))}
          </select>
          <button
            type="button"
            className="telao-botao"
            aria-pressed={pausado}
            onClick={() => setPausado((p) => !p)}
          >
            {pausado ? 'Retomar' : 'Pausar'}
          </button>
          {telaCheia && onSair && (
            <button type="button" className="telao-botao" onClick={onSair}>
              Sair do telão
            </button>
          )}
          {!telaCheia && onAbrirTelaCheia && (
            <button type="button" className="telao-botao telao-botao--primario" onClick={onAbrirTelaCheia}>
              Abrir em tela cheia
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default TelaoOficina;
